import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { useAuthContext } from "@/contexts/auth";
import { activationRpcService } from "@/services/activationRpcService";

interface DealerActivationModalProps {
  dealerId: string;
  dealerName: string;
  // Support both naming conventions
  isOpen?: boolean;
  onClose?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  // Callback per aggiornare lo stato del dealer dopo la chiusura della modale
  onSuccess?: () => void;
}

export function DealerActivationModal({ 
  dealerId, 
  dealerName, 
  isOpen, 
  onClose,
  open,
  onOpenChange,
  onSuccess
}: DealerActivationModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuthContext();

  // Determine if modal should be open using either prop
  const dialogOpen = isOpen !== undefined ? isOpen : open;
  
  // Handle dialog close with either callback
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      if (onClose) {
        onClose();
      } else if (onOpenChange) {
        onOpenChange(false);
      }
    }
  };

  const handleActivationRequest = async () => {
    if (!user) {
      toast({
        title: "Errore",
        description: "Devi effettuare l'accesso per richiedere l'attivazione",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Usa la funzione RPC per richiedere l'attivazione
      const success = await activationRpcService.requestActivation(dealerId, user.id);
      
      if (success) {
        toast({
          title: "Richiesta inviata",
          description: "La tua richiesta di attivazione è stata inviata con successo e verrà esaminata presto",
        });
        
        // Chiama la callback onSuccess se è stata fornita
        if (onSuccess) {
          onSuccess();
        }
        
        if (onClose) {
          onClose();
        } else if (onOpenChange) {
          onOpenChange(false);
        }
        
        // Rimuovo il reindirizzamento a /profile per mantenere l'utente sulla stessa pagina
      } else {
        toast({
          title: "Richiesta non inviata",
          description: "Questo Compro Oro è già attivato o esiste già una richiesta in sospeso",
        });
        
        if (onClose) {
          onClose();
        } else if (onOpenChange) {
          onOpenChange(false);
        }
      }
      
    } catch (error) {
      console.error('Errore durante la richiesta di attivazione:', error);
      toast({
        title: "Errore",
        description: "Si è verificato un errore durante l'invio della richiesta. Riprova più tardi.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AlertDialog open={dialogOpen} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Richiedi attivazione Compro Oro</AlertDialogTitle>
          <AlertDialogDescription>
            Stai per richiedere l'attivazione di <strong>{dealerName}</strong>. 
            Confermando, dichiari di essere il proprietario o un rappresentante 
            autorizzato di questa attività. La richiesta sarà esaminata dal 
            team di amministrazione.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isSubmitting}>Annulla</AlertDialogCancel>
          <Button 
            onClick={handleActivationRequest} 
            disabled={isSubmitting}
            className="bg-primary"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Invio in corso...
              </>
            ) : (
              "Conferma richiesta"
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
